import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import ReportPageWrapper, { StatCard } from "./ReportPageWrapper";
import {
  HandCoins,
  CheckCircle,
  Clock,
  XCircle,
  TrendingUp,
  FileText,
  Download,
  Printer,
  Search,
  RotateCcw,
  Eye,
} from "lucide-react";
import { format, subMonths } from "date-fns";
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ComposedChart, Legend,
} from "recharts";

function CustomTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-slate-700 mb-1">{label}</p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} style={{ color: entry.color }} className="text-xs">
            {entry.name}: {entry.value?.toLocaleString()} FRW
          </p>
        ))}
      </div>
    );
  }
  return null;
}

export default function ContributionsReport() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: contributions, isLoading } = useQuery(
    ["my-contributions-report", user?.id],
    async () => {
      const { data } = await api.get("/contributions/my?limit=1000");
      return Array.isArray(data.data) ? data.data : [];
    },
    { enabled: !!user?.id }
  );

  const allData = contributions || [];

  const filtered = useMemo(() => {
    return allData.filter((c: any) => {
      const status = c.isVerified ? "Verified" : c.status || "Pending";
      const matchStatus = statusFilter === "all" || status === statusFilter;
      const matchSearch = !search ||
        (c.referenceNumber || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.verifiedBy || "").toLowerCase().includes(search.toLowerCase());
      return matchStatus && matchSearch;
    });
  }, [allData, statusFilter, search]);

  const stats = useMemo(() => {
    const total = allData.reduce((s: number, c: any) => s + (parseFloat(c.amount || c.depositAmount) || 0), 0);
    const verified = allData.filter((c: any) => c.isVerified).reduce((s: number, c: any) => s + (parseFloat(c.amount || c.depositAmount) || 0), 0);
    const pending = allData.filter((c: any) => !c.isVerified && c.status !== "Rejected").length;
    const rejected = allData.filter((c: any) => c.status === "Rejected").length;
    const verifiedCount = allData.filter((c: any) => c.isVerified).length;
    const totalCount = allData.length;
    const avg = totalCount > 0 ? total / totalCount : 0;
    return { total, verified, verifiedCount, pending, rejected, avg, totalCount };
  }, [allData]);

  const timelineData = useMemo(() => {
    const months: Record<string, { contributed: number; verified: number }> = {};
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = subMonths(now, i);
      const key = format(d, "MMM yy");
      months[key] = { contributed: 0, verified: 0 };
    }
    allData.forEach((c: any) => {
      const d = new Date(c.contributionDate || c.createdAt);
      const key = format(d, "MMM yy");
      if (months[key]) {
        const amt = parseFloat(c.amount || c.depositAmount) || 0;
        months[key].contributed += amt;
        if (c.isVerified) months[key].verified += amt;
      }
    });
    return Object.entries(months).map(([label, vals]) => ({ label, ...vals }));
  }, [allData]);

  const formatCurrency = (val: number) => (val || 0).toLocaleString() + " FRW";

  const handleReset = () => { setSearch(""); setStatusFilter("all"); };

  const handleExport = () => {
    const headers = ["Date", "Amount", "Status", "Verified By", "Reference"];
    const rows = filtered.map((c: any) => [
      format(new Date(c.contributionDate || c.createdAt), "yyyy-MM-dd"),
      parseFloat(c.amount || c.depositAmount) || 0,
      c.isVerified ? "Verified" : c.status || "Pending",
      c.verifiedBy || "N/A", c.referenceNumber || "N/A",
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `contributions-report-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl" />)}
        </div>
        <div className="h-64 bg-slate-100 rounded-xl" />
      </div>
    );
  }

  return (
    <ReportPageWrapper
      title="Contributions Report"
      breadcrumbItems={[
        { label: "Dashboard", path: "/member/dashboard" },
        { label: "Member Reports", path: "/member/reports" },
        { label: "Contributions Report" },
      ]}
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Total Contributions" value={formatCurrency(stats.total)} icon={HandCoins} color="#009A44" />
        <StatCard label="Verified" value={formatCurrency(stats.verified)} icon={CheckCircle} color="#0EA5E9" />
        <StatCard label="Pending" value={stats.pending.toString()} icon={Clock} color="#EAB308" />
        <StatCard label="Rejected" value={stats.rejected.toString()} icon={XCircle} color="#EF4444" />
        <StatCard label="Average per Record" value={formatCurrency(stats.avg)} icon={TrendingUp} color="#8B5CF6" />
        <StatCard label="Total Records" value={stats.totalCount.toString()} icon={FileText} color="#64748B" />
      </div>

      {/* Chart */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <HandCoins className="h-4 w-4 text-[#009A44]" />
          Contribution Timeline
        </h3>
        {timelineData.some(d => d.contributed > 0) ? (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={timelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="contributed" name="Contributed" fill="#009A44" radius={[4, 4, 0, 0]} opacity={0.7} />
                <Line type="monotone" dataKey="verified" name="Verified" stroke="#0EA5E9" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-10 bg-slate-50 rounded-lg">
            <HandCoins className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-500">No contribution data yet</p>
            <p className="text-xs text-slate-400 mt-1">Make your first contribution to see trends.</p>
          </div>
        )}
      </div>

      {/* Contributions Table Card */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-100">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search contributions..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="h-9 px-3 text-xs border border-slate-200 rounded-lg bg-white text-slate-600"
            >
              <option value="all">All Status</option>
              <option value="Verified">Verified</option>
              <option value="Pending">Pending</option>
              <option value="Rejected">Rejected</option>
            </select>
            <Button variant="ghost" size="sm" onClick={handleReset} className="text-slate-500 h-9">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()} className="h-9">
              <Printer className="h-3.5 w-3.5 mr-1" /> Print
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} className="h-9">
              <Download className="h-3.5 w-3.5 mr-1" /> Export
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#009A44]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Date</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Verified By</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Reference</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-white uppercase tracking-wider">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.slice(0, 50).map((c: any, idx: number) => (
                <tr key={c.id || idx} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                    {format(new Date(c.contributionDate || c.createdAt), "MMM dd, yyyy")}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">
                    {(parseFloat(c.amount || c.depositAmount) || 0).toLocaleString()} FRW
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant={c.isVerified ? "success" : c.status === "Rejected" ? "destructive" : "secondary"}
                      className="text-[10px]"
                    >
                      {c.isVerified ? "Verified" : c.status || "Pending"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.verifiedBy || "N/A"}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs max-w-[100px] truncate">
                    {c.referenceNumber || "N/A"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {c.documentReceipt || c.bankReceipt ? (
                      <a href={c.documentReceipt || c.bankReceipt} target="_blank" rel="noopener noreferrer">
                        <Eye className="h-3.5 w-3.5 text-blue-500 inline cursor-pointer" />
                      </a>
                    ) : (
                      <span className="text-slate-300 text-xs">--</span>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                    No contribution records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </ReportPageWrapper>
  );
}
